import { User } from 'lucide-react';
import { LoadMore } from '@/components/loadMore/loadMore';
import type { SearchSuggestions } from '@/services/listingService';
import { tr } from '@/i18n/tr';
import type { Category, Listing, Producer, Product } from '@/types/domain';
import styles from './searchSuggestions.module.scss';
import { formatPrice } from '@/util/number';
import { formatLocation } from '@/util/formatLocation';
import { PhotoFallback } from '@/components/photoFallback/photoFallback';
import { useListingIcons } from '@/hooks/useListingIcons';
import { ProduceIcon } from '@/components/produceIcon/produceIcon';
import { iconSourceOf } from '@/components/produceIcon/produceArt';

export interface SearchSuggestionsPanelProps {
  data: SearchSuggestions;
  /**
   * Categories whose name matches the query. Matched on the client: the whole
   * category list is already loaded for the chip row, so asking the server for
   * a subset of what we hold would be a round trip for nothing.
   */
  categories: Category[];
  /** Matching subcategories, from the same locally held list as the menu. */
  products: Product[];
  /** A page is in flight; the button says so instead of looking inert. */
  busy?: boolean;
  onLoadMore: () => void;
  onPickListing: (listing: Listing) => void;
  onPickProducer: (producer: Producer) => void;
  onPickCategory: (category: Category) => void;
  onPickProduct: (product: Product) => void;
}

/** Autocomplete dropdown under the search field: listings, producers, categories. */
export function SearchSuggestionsPanel({
  data,
  categories,
  products,
  busy = false,
  onLoadMore,
  onPickListing,
  onPickProducer,
  onPickCategory,
  onPickProduct,
}: SearchSuggestionsPanelProps) {
  const isEmpty =
    data.listings.length === 0 &&
    data.producers.length === 0 &&
    categories.length === 0 &&
    products.length === 0;
  const iconFor = useListingIcons();

  // Server-paged: total is every match, so this is what is genuinely still
  // hidden — not what a client-side window is holding back.
  const remaining = data.total - data.listings.length;

  return (
    <div className={styles.panel}>
      {isEmpty && <p className={styles.empty}>{tr.explore.suggest.empty}</p>}

      {data.listings.length > 0 && (
        <>
          <p className={styles.group}>{tr.explore.suggest.listings}</p>
          {data.listings.map((l) => (
            <button
              key={l.id}
              type="button"
              className={styles.row}
              onClick={() => onPickListing(l)}
            >
              {l.photos[0] ? (
                <img src={l.photos[0].url} alt="" loading="lazy" />
              ) : (
                <PhotoFallback className={styles.rowThumb} icon={iconFor(l)} />
              )}
              <span className={styles.rowMain}>
                <strong>{l.title}</strong>
                {/* Full location, province included: a search spans the whole
                    country, so "Kılan" alone cannot be told apart from a
                    mahalle of the same name three provinces away.
                    No approximate badge here on purpose: this is a fast,
                    single-line type-ahead row with many results stacked tightly,
                    and a second chip would crowd it. The badge shows once the
                    listing is opened. */}
                <small>{formatLocation(l)}</small>
              </span>
              {/* search results are thin (no price, anti-scrape) — hide rather than show a fake 0 TL */}
              {l.price > 0 && (
                <em className={styles.price}>
                  {formatPrice(l.price)} TL/{tr.listing.priceUnit[l.priceUnit]}
                </em>
              )}
            </button>
          ))}
          <LoadMore
            remaining={remaining}
            onClick={onLoadMore}
            className={`${styles.more} ${busy ? styles.moreBusy : ''}`}
          />
        </>
      )}

      {data.producers.length > 0 && (
        <>
          <p className={styles.group}>{tr.explore.suggest.producers}</p>
          {data.producers.map((p) => (
            <button
              key={p.id}
              type="button"
              className={styles.row}
              onClick={() => onPickProducer(p)}
            >
              <span className={styles.avatar} aria-hidden>
                <User />
              </span>
              <span className={styles.rowMain}>
                <strong>{p.displayName}</strong>
                <small>{formatLocation(p)}</small>
              </span>
            </button>
          ))}
        </>
      )}

      {products.length > 0 && (
        <>
          <p className={styles.group}>{tr.explore.suggest.products}</p>
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              className={styles.row}
              onClick={() => onPickProduct(p)}
            >
              <span className={styles.avatar} aria-hidden>
                <ProduceIcon icon={iconSourceOf(p)} />
              </span>
              <span className={styles.rowMain}>
                <strong>{p.name}</strong>
              </span>
            </button>
          ))}
        </>
      )}

      {categories.length > 0 && (
        <>
          <p className={styles.group}>{tr.explore.suggest.categories}</p>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={styles.row}
              onClick={() => onPickCategory(c)}
            >
              <span className={styles.avatar} aria-hidden>
                <ProduceIcon icon={iconSourceOf(c)} />
              </span>
              <span className={styles.rowMain}>
                <strong>{c.name}</strong>
              </span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
